<template>
  <div
    class="h-[100dvh] w-full overflow-hidden bg-zinc-50 text-zinc-900 selection:bg-indigo-500/30 dark:bg-[#07070a] dark:text-zinc-200 focus:outline-none"
    style="font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Inter, Helvetica, Arial, sans-serif;"
    @keydown.esc="showPresetModal = false" tabindex="0">

    <div class="h-full w-full flex flex-col lg:grid lg:grid-cols-[360px_1fr]">

      <aside
        class="lg:h-full lg:min-h-0 border-r border-black/5 bg-white/70 backdrop-blur-xl flex flex-col min-w-0 lg:overflow-hidden dark:border-white/5 dark:bg-[#0b0b0f] order-2 lg:order-1 transition-all duration-300 ease-in-out"
        :class="mobileMenuOpen ? 'fixed inset-0 z-40 bg-white dark:bg-[#0b0b0f] overflow-y-auto' : 'hidden lg:flex'">

        <div
          class="h-16 px-4 flex-shrink-0 flex items-center justify-between border-b border-black/5 bg-white/80 backdrop-blur-xl dark:border-white/5 dark:bg-[#0b0b0f]/80 sticky top-0 z-10 lg:relative">
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="relative w-9 h-9 rounded-xl bg-black/[0.03] border border-black/10 grid place-items-center overflow-hidden dark:bg-white/[0.04] dark:border-white/10 shadow-sm">
              <div
                class="absolute inset-0 opacity-70 bg-[radial-gradient(circle_at_30%_30%,rgba(99,102,241,0.45),transparent_55%),radial-gradient(circle_at_70%_70%,rgba(168,85,247,0.35),transparent_55%)] animate-pulse-slow">
              </div>
              <div
                class="relative w-2.5 h-2.5 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 shadow-inner">
              </div>
            </div>
            <div class="min-w-0">
              <p class="text-[10px] uppercase tracking-[0.26em] text-zinc-500">Palette Studio</p>
              <p class="text-sm font-semibold tracking-tight text-zinc-900 truncate dark:text-white">Gradient Board</p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <button @click="resetPalette"
              class="h-9 px-2.5 rounded-xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-zinc-700 hover:text-zinc-900 transition-all duration-200 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-300 dark:hover:text-white"
              title="Reset">
              <span class="material-symbols-rounded text-[18px]">restart_alt</span>
            </button>

            <button @click="mobileMenuOpen = false"
              class="lg:hidden h-9 w-9 rounded-xl border border-black/10 bg-black/[0.03] grid place-items-center dark:border-white/10 dark:bg-white/[0.03] active:scale-95 transition-transform">
              <span class="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>
        </div>

        <div class="flex-1 min-h-0 lg:overflow-y-auto px-4 py-4 space-y-5 custom-scrollbar pb-24 lg:pb-10">
          <div
            class="rounded-2xl border border-black/10 bg-white/70 overflow-hidden dark:border-white/10 dark:bg-white/[0.02] shadow-sm transition-all duration-300 hover:shadow-md">
            <div class="px-4 py-3 flex items-center justify-between border-b border-black/5 dark:border-white/5">
              <div class="flex items-center gap-2">
                <span class="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Layers</span>
                <span class="text-[10px] text-zinc-500/80 dark:text-zinc-600">{{ colors.length }} / 6</span>
              </div>

              <button v-if="colors.length < 6" @click="addColor"
                class="h-8 px-2.5 rounded-xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-[11px] text-zinc-800 hover:text-zinc-900 transition-all duration-200 inline-flex items-center gap-2 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-300 dark:hover:text-white">
                <span class="material-symbols-rounded text-[16px]">add</span>
                Add step
              </button>
            </div>

            <div class="p-3">
              <transition-group name="list" tag="div" class="space-y-2 relative">
                <div v-for="(c, idx) in colors" :key="c.id"
                  class="group relative rounded-2xl border border-black/10 bg-white hover:bg-black/[0.02] transition-all duration-200 dark:border-white/10 dark:bg-[#0b0b0f] dark:hover:bg-white/[0.03] hover:shadow-sm hover:z-10">
                  <div class="p-3 flex items-center gap-3">
                    <div
                      class="relative w-11 h-11 rounded-xl overflow-hidden border border-black/10 ring-1 ring-inset ring-black/5 dark:border-white/10 dark:ring-white/5 shadow-sm group-hover:scale-105 transition-transform duration-300">
                      <div class="absolute inset-0"
                        :style="{ backgroundColor: normalizeHex(c.hex) || (isDark ? '#0b0b0f' : '#ffffff') }"></div>
                      <input type="color" :value="c.hex"
                        class="absolute inset-[-40%] w-[180%] h-[180%] cursor-pointer opacity-0"
                        @input="onColorPicker(idx, $event.target.value)" />
                      <div
                        class="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-lg bg-black/45 border border-white/10 grid place-items-center opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none backdrop-blur-sm">
                        <span class="material-symbols-rounded text-[14px] text-white/90">palette</span>
                      </div>
                    </div>

                    <div class="flex-1 min-w-0">
                      <input v-model="c.name" placeholder="Untitled"
                        class="w-full bg-transparent text-[12px] font-semibold tracking-tight text-zinc-900 placeholder-zinc-400 focus:outline-none focus:text-indigo-600 transition-colors duration-200 dark:text-zinc-100 dark:placeholder-zinc-600 dark:focus:text-indigo-400"
                        @input="markCustom(idx)" />

                      <div class="mt-1 flex flex-col gap-0.5">
                        <input :value="c.hex.toUpperCase()" maxlength="7"
                          class="w-full bg-transparent text-[11px] font-mono text-zinc-600 focus:outline-none focus:text-indigo-600 transition-colors duration-200 dark:text-zinc-400 dark:focus:text-indigo-300"
                          @input="onHexInput(idx, $event.target.value)" />

                        <div
                          class="w-fit rounded bg-black/[0.04] px-1.5 py-0.5 text-[9px] font-mono font-medium text-zinc-500 dark:bg-white/[0.06] dark:text-zinc-400">
                          {{ rgbLabel(c.hex) }}
                        </div>
                      </div>
                    </div>

                    <div
                      class="flex items-center gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200">
                      <div class="flex flex-col">
                        <button :disabled="idx === 0" @click="moveColor(idx, -1)"
                          class="h-7 w-7 rounded-lg border border-transparent hover:border-black/10 hover:bg-black/[0.03] active:scale-90 text-zinc-500 hover:text-zinc-900 disabled:opacity-0 transition-all duration-200 grid place-items-center dark:hover:border-white/10 dark:hover:bg-white/[0.04] dark:hover:text-white"
                          title="Move up">
                          <span class="material-symbols-rounded text-[18px]">keyboard_arrow_up</span>
                        </button>
                        <button :disabled="idx === colors.length - 1" @click="moveColor(idx, 1)"
                          class="h-7 w-7 rounded-lg border border-transparent hover:border-black/10 hover:bg-black/[0.03] active:scale-90 text-zinc-500 hover:text-zinc-900 disabled:opacity-0 transition-all duration-200 grid place-items-center dark:hover:border-white/10 dark:hover:bg-white/[0.04] dark:hover:text-white"
                          title="Move down">
                          <span class="material-symbols-rounded text-[18px]">keyboard_arrow_down</span>
                        </button>
                      </div>

                      <button :disabled="colors.length <= 2" @click="removeColor(idx)"
                        class="h-9 w-9 rounded-xl border border-transparent hover:border-black/10 hover:bg-black/[0.03] active:scale-90 text-zinc-500 hover:text-rose-600 disabled:hidden transition-all duration-200 grid place-items-center dark:hover:border-white/10 dark:hover:bg-white/[0.04] dark:text-zinc-600 dark:hover:text-rose-300"
                        title="Remove">
                        <span class="material-symbols-rounded text-[18px]">remove</span>
                      </button>
                    </div>
                  </div>

                  <div class="px-3 pb-3">
                    <div
                      class="h-1.5 w-full rounded-full bg-black/[0.04] overflow-hidden border border-black/5 dark:bg-white/[0.04] dark:border-white/5">
                      <div class="h-full rounded-full transition-all duration-300 ease-out"
                        :style="{ width: `${Math.max(8, Math.min(100, (idx / Math.max(1, colors.length - 1)) * 100))}%`, background: 'linear-gradient(90deg, rgba(99,102,241,0.65), rgba(168,85,247,0.55))' }">
                      </div>
                    </div>
                  </div>
                </div>
              </transition-group>

              <div v-if="colors.length < 2"
                class="p-3 rounded-2xl border border-black/10 bg-black/[0.02] text-[11px] text-zinc-600 dark:border-white/10 dark:bg-white/[0.02] dark:text-zinc-500 animate-fade-in">
                Add at least 2 steps to build a gradient.
              </div>
            </div>
          </div>

          <div
            class="rounded-2xl border border-black/10 bg-white/70 overflow-hidden dark:border-white/10 dark:bg-white/[0.02] shadow-sm hover:shadow-md transition-all duration-300">
            <div class="px-4 py-3 border-b border-black/5 flex items-center justify-between dark:border-white/5">
              <span class="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Interpolation</span>
              <span class="text-[10px] font-mono text-zinc-500 dark:text-zinc-600 transition-all duration-300">{{
                gradientMode === 'radial' ?
                  'Radial' : `${gradientAngle}°` }}</span>
            </div>

            <div class="p-4">
              <div
                class="relative h-12 rounded-2xl border border-black/10 overflow-hidden bg-white dark:border-white/10 dark:bg-white/[0.02] shadow-inner">
                <div class="absolute inset-0 transition-all duration-500" :style="{ background: gradientString }"></div>
                <div class="absolute inset-0 noise-soft"></div>
                <div v-for="(stop, i) in gradientStops" :key="'m-' + i"
                  class="absolute top-2 bottom-2 w-px bg-white/40 mix-blend-overlay transition-all duration-500 ease-out"
                  :style="{ left: stop.position + '%' }"></div>
              </div>

              <div
                class="mt-3 flex items-center justify-between text-[10px] font-mono text-zinc-500 dark:text-zinc-600">
                <span>0%</span>
                <span>100%</span>
              </div>

              <div class="mt-4 grid grid-cols-2 gap-2">
                <button @click="showPresetModal = true"
                  class="h-10 rounded-2xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-[12px] font-medium text-zinc-800 hover:text-zinc-900 transition-all duration-200 inline-flex items-center justify-center gap-2 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-200 dark:hover:text-white">
                  <span class="material-symbols-rounded text-[18px]">grid_view</span>
                  Library
                </button>
                <button @click="randomizeAngle"
                  class="h-10 rounded-2xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-[12px] font-medium text-zinc-800 hover:text-zinc-900 transition-all duration-200 inline-flex items-center justify-center gap-2 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-200 dark:hover:text-white">
                  <span class="material-symbols-rounded text-[18px]">shuffle</span>
                  Angle
                </button>
              </div>
            </div>
          </div>

          <div
            class="rounded-2xl border border-black/10 bg-white/70 overflow-hidden dark:border-white/10 dark:bg-white/[0.02] shadow-sm hover:shadow-md transition-all duration-300">
            <div class="px-4 py-3 border-b border-black/5 flex items-center justify-between dark:border-white/5">
              <span class="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Export</span>
              <span class="text-[10px] text-zinc-500 dark:text-zinc-600">PNG</span>
            </div>
            <div class="p-4 grid grid-cols-2 gap-2">
              <button @click="exportGradientPng"
                class="h-10 rounded-2xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-[12px] font-medium text-zinc-800 hover:text-zinc-900 transition-all duration-200 inline-flex items-center justify-center gap-2 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-200 dark:hover:text-white">
                <span class="material-symbols-rounded text-[18px]">image</span>
                Gradient
              </button>
              <button @click="exportPng"
                class="h-10 rounded-2xl border border-indigo-500/20 bg-indigo-500/10 hover:bg-indigo-500/15 active:scale-95 text-[12px] font-semibold text-indigo-700 hover:text-indigo-800 transition-all duration-200 inline-flex items-center justify-center gap-2 dark:border-indigo-400/20 dark:bg-indigo-500/15 dark:hover:bg-indigo-500/20 dark:text-indigo-200 dark:hover:text-white">
                <span class="material-symbols-rounded text-[18px]">download</span>
                Board
              </button>
            </div>
          </div>
        </div>

        <div
          class="px-4 py-4 flex-shrink-0 border-t border-black/5 bg-white/70 backdrop-blur-xl dark:border-white/5 dark:bg-[#0b0b0f]/70 hidden lg:block">
          <div class="rounded-2xl border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.02]">
            <p class="text-[10px] uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-600">Tip</p>
            <p class="mt-1 text-[11px] text-zinc-600 leading-relaxed dark:text-zinc-400">
              Hover a step to reorder or remove. Use the library to instantly load premium gradients.
            </p>
          </div>
        </div>
      </aside>

      <main
        class="h-full min-w-0 flex flex-col bg-zinc-50 text-zinc-900 dark:bg-[#07070a] dark:text-zinc-100 overflow-hidden order-1 lg:order-2">
        <header
          class="h-16 px-4 lg:px-6 flex-shrink-0 flex items-center justify-between border-b border-black/5 bg-white/70 backdrop-blur-xl dark:border-white/5 dark:bg-[#07070a]/70 z-20 transition-all duration-300">
          <div class="flex items-center gap-3">
            <button @click="mobileMenuOpen = true"
              class="lg:hidden h-10 w-10 rounded-xl border border-black/10 bg-black/[0.03] grid place-items-center dark:border-white/10 dark:bg-white/[0.03] active:scale-95 transition-transform">
              <span class="material-symbols-rounded text-[20px]">menu</span>
            </button>

            <div
              class="hidden sm:inline-flex items-center rounded-2xl border border-black/10 bg-black/[0.03] p-1 dark:border-white/10 dark:bg-white/[0.03] transition-colors">
              <button @click="gradientMode = 'linear'"
                class="h-9 px-4 rounded-xl text-[12px] font-semibold transition-all duration-200" :class="gradientMode === 'linear'
                  ? 'bg-white shadow-sm text-zinc-900 ring-1 ring-black/5 dark:bg-white/10 dark:text-white dark:ring-white/10'
                  : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'">
                Linear
              </button>
              <button @click="gradientMode = 'radial'"
                class="h-9 px-4 rounded-xl text-[12px] font-semibold transition-all duration-200" :class="gradientMode === 'radial'
                  ? 'bg-white shadow-sm text-zinc-900 ring-1 ring-black/5 dark:bg-white/10 dark:text-white dark:ring-white/10'
                  : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'">
                Radial
              </button>
            </div>

            <div class="hidden lg:flex items-center gap-3 ml-2">
              <div
                class="h-9 px-3 rounded-2xl border border-black/10 bg-black/[0.03] flex items-center gap-3 w-[240px] xl:w-[320px] dark:border-white/10 dark:bg-white/[0.03] transition-colors hover:border-black/20 dark:hover:border-white/20">
                <span class="text-[10px] uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-600">Angle</span>
                <input type="range" min="0" max="360" v-model.number="gradientAngle"
                  :disabled="gradientMode === 'radial'" class="flex-1 h-1 rounded-full bg-black/10 appearance-none disabled:opacity-40
                         [&::-webkit-slider-thumb]:appearance-none
                         [&::-webkit-slider-thumb]:w-3
                         [&::-webkit-slider-thumb]:h-3
                         [&::-webkit-slider-thumb]:rounded-full
                         [&::-webkit-slider-thumb]:bg-zinc-900
                         [&::-webkit-slider-thumb]:shadow-sm
                         [&::-webkit-slider-thumb]:transition-transform
                         [&::-webkit-slider-thumb]:hover:scale-125
                         dark:bg-white/10
                         dark:[&::-webkit-slider-thumb]:bg-white" />
                <span class="text-[11px] w-12 text-right font-mono text-zinc-600 dark:text-zinc-400">
                  {{ gradientMode === 'radial' ? 'N/A' : `${gradientAngle}°` }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2">

            <router-link to="/dashboard/hub"
              class="h-10 w-10 rounded-2xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-zinc-700 hover:text-indigo-600 transition-all duration-200 grid place-items-center
             dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-300 dark:hover:text-indigo-400" title="Tutorial Hub">
              <span class="material-symbols-rounded text-[20px]">school</span>
            </router-link>

            <button @click="showPresetModal = true"
              class="h-10 px-3 lg:px-4 rounded-2xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-[12px] font-medium text-zinc-800 hover:text-zinc-900 transition-all duration-200 inline-flex items-center gap-2
             dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-200 dark:hover:text-white">
              <span class="material-symbols-rounded text-[18px]">auto_awesome</span>
              <span class="hidden sm:inline">Presets</span>
            </button>

            <button @click="exportPng"
              class="h-10 px-3 lg:px-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/10 hover:bg-indigo-500/15 active:scale-95 text-[12px] font-semibold text-indigo-700 hover:text-indigo-800 transition-all duration-200 inline-flex items-center gap-2
             dark:border-indigo-400/20 dark:bg-indigo-500/20 dark:hover:bg-indigo-500/25 dark:text-indigo-100 dark:hover:text-white">
              <span class="material-symbols-rounded text-[18px]">download</span>
              <span class="hidden sm:inline">Export</span>
            </button>
          </div>
        </header>

        <div
          class="sm:hidden px-4 py-2 border-b border-black/5 bg-white/50 backdrop-blur-md dark:border-white/5 dark:bg-[#07070a]/50 flex items-center justify-between gap-3">
          <div
            class="inline-flex items-center rounded-2xl border border-black/10 bg-black/[0.03] p-1 dark:border-white/10 dark:bg-white/[0.03]">
            <button @click="gradientMode = 'linear'"
              class="h-8 px-3 rounded-xl text-[11px] font-semibold transition-all duration-200" :class="gradientMode === 'linear'
                ? 'bg-white shadow-sm text-zinc-900 ring-1 ring-black/5 dark:bg-white/10 dark:text-white dark:ring-white/10'
                : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'">
              Linear
            </button>
            <button @click="gradientMode = 'radial'"
              class="h-8 px-3 rounded-xl text-[11px] font-semibold transition-all duration-200" :class="gradientMode === 'radial'
                ? 'bg-white shadow-sm text-zinc-900 ring-1 ring-black/5 dark:bg-white/10 dark:text-white dark:ring-white/10'
                : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'">
              Radial
            </button>
          </div>

          <div class="flex-1 flex items-center gap-2 min-w-0" v-if="gradientMode === 'linear'">
            <input type="range" min="0" max="360" v-model.number="gradientAngle" class="flex-1 h-1 rounded-full bg-black/10 appearance-none
                         [&::-webkit-slider-thumb]:appearance-none
                         [&::-webkit-slider-thumb]:w-3
                         [&::-webkit-slider-thumb]:h-3
                         [&::-webkit-slider-thumb]:rounded-full
                         [&::-webkit-slider-thumb]:bg-zinc-900
                         dark:bg-white/10
                         dark:[&::-webkit-slider-thumb]:bg-white" />
            <span class="text-[10px] font-mono w-8 text-right">{{ gradientAngle }}°</span>
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto relative pb-32 lg:pb-32 custom-scrollbar scroll-smooth">

          <div class="relative mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
            <section
              class="rounded-3xl border border-black/10 bg-white/70 overflow-hidden shadow-[0_30px_120px_-90px_rgba(0,0,0,0.25)]
                            dark:border-white/10 dark:bg-white/[0.02] dark:shadow-[0_30px_120px_-80px_rgba(0,0,0,0.85)]
                            transition-all duration-500 hover:shadow-[0_40px_140px_-80px_rgba(0,0,0,0.3)] dark:hover:shadow-[0_40px_140px_-70px_rgba(0,0,0,0.95)] animate-fade-in-up">
              <div
                class="px-4 sm:px-6 py-4 border-b border-black/5 flex items-center justify-between dark:border-white/5">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Preview</p>
                  <p class="font-semibold tracking-tight text-sm sm:text-base">Gradient Render</p>
                </div>
                <button @click="exportGradientPng" class="h-9 sm:h-10 px-3 sm:px-4 rounded-2xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-[11px] sm:text-[12px] font-medium transition-all duration-200
                         dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]">
                  Save PNG
                </button>
              </div>

              <div class="p-4 sm:p-6">
                <div ref="gradientRef"
                  class="w-full aspect-[4/3] sm:aspect-[21/9] rounded-3xl overflow-hidden border border-black/10 dark:border-white/10 relative shadow-inner">
                  <div class="absolute inset-0 transition-all duration-700 ease-in-out"
                    :style="{ background: gradientString }"></div>
                  <div class="absolute inset-0 noise-soft"></div>
                </div>
              </div>
            </section>

            <section
              class="rounded-3xl border border-black/10 bg-white/70 overflow-hidden shadow-[0_30px_120px_-90px_rgba(0,0,0,0.25)]
                            dark:border-white/10 dark:bg-white/[0.02] dark:shadow-[0_30px_120px_-85px_rgba(0,0,0,0.85)]
                            transition-all duration-500 hover:shadow-[0_40px_140px_-80px_rgba(0,0,0,0.3)] dark:hover:shadow-[0_40px_140px_-70px_rgba(0,0,0,0.95)] animate-fade-in-up delay-100">
              <div
                class="px-4 sm:px-6 py-4 border-b border-black/5 flex items-center justify-between dark:border-white/5">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Board</p>
                  <p class="font-semibold tracking-tight text-sm sm:text-base">Export Composition</p>
                </div>
                <button @click="exportPng" class="h-9 sm:h-10 px-3 sm:px-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/10 hover:bg-indigo-500/15 active:scale-95 text-[11px] sm:text-[12px] font-semibold transition-all duration-200
                         dark:border-indigo-400/20 dark:bg-indigo-500/20 dark:hover:bg-indigo-500/25">
                  Export PNG
                </button>
              </div>

              <div class="p-4 sm:p-6">
                <div ref="exportRef"
                  class="rounded-3xl overflow-hidden border border-black/10 dark:border-white/10 shadow-sm">
                  <div class="relative h-32 sm:h-44 group">
                    <div class="absolute inset-0 transition-all duration-700 ease-in-out"
                      :style="{ backgroundImage: gradientString }"></div>
                    <div class="absolute inset-0 noise-soft"></div>
                  </div>

                  <div class="p-4 sm:p-5 bg-white dark:bg-[#0b0b0f] grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div v-for="c in normalizedColors" :key="c.hex"
                      class="rounded-2xl border border-black/10 bg-black/[0.02] overflow-hidden dark:border-white/10 dark:bg-white/[0.02] flex sm:block items-center sm:items-stretch transition-transform duration-300 hover:scale-[1.02]">
                      <div
                        class="h-10 w-10 sm:h-12 sm:w-full flex-shrink-0 sm:flex-shrink transition-colors duration-300"
                        :style="{ backgroundColor: c.hex }"></div>
                      <div class="px-3 py-2 min-w-0">
                        <p class="text-[11px] font-mono font-semibold truncate select-all">{{ c.hex }}</p>
                        <p class="text-[10px] text-zinc-500 truncate">{{ findNameForHex(c.hex) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>

    <transition name="modal">
      <div v-if="showPresetModal" class="fixed inset-0 z-50">
        <div class="absolute inset-0 bg-black/55 backdrop-blur-md transition-opacity duration-300"
          @click="showPresetModal = false"></div>

        <div class="absolute inset-0 flex items-start justify-center pt-4 sm:pt-[9vh] px-2 sm:px-4 pointer-events-none">
          <div
            class="w-full max-w-4xl max-h-[95dvh] sm:max-h-[85vh] flex flex-col rounded-3xl border border-black/10 bg-white/90 shadow-[0_60px_200px_-120px_rgba(0,0,0,0.35)] overflow-hidden dark:border-white/10 dark:bg-[#0f0f14] dark:shadow-[0_60px_200px_-120px_rgba(0,0,0,0.9)] pointer-events-auto transform transition-all duration-300">
            <div
              class="px-4 sm:px-5 py-4 border-b border-black/5 flex items-center justify-between gap-3 dark:border-white/5 bg-white/50 backdrop-blur-md dark:bg-[#0f0f14]/50 z-10">
              <div class="flex items-center gap-3 min-w-0 flex-1">
                <div
                  class="hidden sm:grid h-10 w-10 rounded-2xl border border-black/10 bg-black/[0.03] place-items-center dark:border-white/10 dark:bg-white/[0.03]">
                  <span class="material-symbols-rounded text-[20px] text-zinc-800 dark:text-white/80">search</span>
                </div>
                <div class="min-w-0 flex-1">
                  <input v-model="presetQuery" type="text" placeholder="Search presets..."
                    class="w-full bg-transparent outline-none border-none text-[13px] text-zinc-900 placeholder-zinc-400 dark:text-white dark:placeholder-zinc-500"
                    autofocus />
                  <p class="mt-0.5 text-[10px] text-zinc-500 dark:text-zinc-500 truncate">
                    Tap to apply. ESC to close.
                  </p>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <button @click="showPresetModal = false"
                  class="h-9 w-9 rounded-2xl border border-black/10 bg-black/[0.03] hover:bg-black/[0.05] active:scale-95 text-zinc-700 hover:text-zinc-900 transition-all duration-200 grid place-items-center dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:text-zinc-300 dark:hover:text-white">
                  <span class="material-symbols-rounded text-[18px]">close</span>
                </button>
              </div>
            </div>

            <div class="flex-1 p-4 sm:p-5 overflow-y-auto custom-scrollbar">
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <button v-for="preset in filteredPresets" :key="preset.id" @click="applyPreset(preset)" class="text-left rounded-3xl border border-black/10 bg-white hover:bg-black/[0.02] active:scale-[0.98] transition-all duration-200 overflow-hidden group
                         dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-white/[0.04]">
                  <div class="relative h-24 sm:h-28 border-b border-black/5 overflow-hidden dark:border-white/5">
                    <div class="absolute inset-0 transition-transform duration-700 group-hover:scale-110"
                      :style="{ backgroundImage: buildPresetGradient(preset) }"></div>
                    <div class="absolute inset-0 noise-soft opacity-80"></div>
                    <div class="absolute inset-0 bg-black/10"></div>
                    <div
                      class="absolute top-3 right-3 h-7 sm:h-8 px-2.5 rounded-2xl border border-white/15 bg-black/35 backdrop-blur-xl inline-flex items-center gap-2 shadow-sm">
                      <span class="w-1.5 h-1.5 rounded-full shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                        :class="preset.mode === 'radial' ? 'bg-cyan-300' : 'bg-indigo-300'"></span>
                      <span class="text-[10px] uppercase tracking-[0.18em] text-white/90">
                        {{ preset.mode }}
                      </span>
                    </div>
                  </div>

                  <div class="p-3 sm:p-4">
                    <div class="min-w-0">
                      <p
                        class="text-[12px] font-semibold tracking-tight text-zinc-900 group-hover:text-zinc-950 truncate dark:text-zinc-100 dark:group-hover:text-white transition-colors">
                        {{ preset.name }}
                      </p>
                      <p class="mt-1 text-[10px] font-mono text-zinc-500 dark:text-zinc-500">
                        {{ preset.mode === 'radial' ? 'circle' : `${preset.angle}deg` }} · {{ preset.colors.length }}
                        stops
                      </p>
                    </div>

                    <div class="mt-3 flex items-center gap-1.5 sm:gap-2">
                      <div v-for="(c, i) in preset.colors.slice(0, 5)" :key="preset.id + '-sw-' + i"
                        class="h-5 w-5 sm:h-6 sm:w-6 rounded-lg sm:rounded-xl border border-black/10 overflow-hidden dark:border-white/10 shadow-sm"
                        :title="c">
                        <div class="h-full w-full" :style="{ backgroundColor: c }"></div>
                      </div>
                      <span v-if="preset.colors.length > 5"
                        class="text-[10px] font-mono text-zinc-500 dark:text-zinc-600">
                        +{{ preset.colors.length - 5 }}
                      </span>
                    </div>
                  </div>
                </button>
              </div>

              <div v-if="!filteredPresets.length"
                class="mt-10 rounded-3xl border border-black/10 bg-white p-8 text-center dark:border-white/10 dark:bg-white/[0.02] animate-fade-in">
                <p class="text-zinc-900 font-semibold dark:text-white">No presets found</p>
                <p class="mt-1 text-[12px] text-zinc-500 dark:text-zinc-400">Try a different query.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'

let idCounter = 1

const gradientMode = ref('linear')
const gradientAngle = ref(135)
const showPresetModal = ref(false)
const presetQuery = ref('')
const mobileMenuOpen = ref(false)

const exportRef = ref(null)
const gradientRef = ref(null)

const isDark = ref(false)

const baseSeed = ['#09090B', '#27272A', '#6366F1', '#A855F7']

const gradientPresets = [
  { id: 'g001', name: 'Neon Rift', mode: 'linear', angle: 135, colors: ['#020617', '#1E3A8A', '#7C3AED', '#FACC15'] },
  { id: 'g002', name: 'Crimson Shock', mode: 'radial', angle: 90, colors: ['#020617', '#7F1D1D', '#DC2626', '#FB7185'] },
  { id: 'g003', name: 'Electric Aurora', mode: 'linear', angle: 120, colors: ['#020617', '#0F766E', '#2DD4BF', '#A7F3D0'] },
  { id: 'g004', name: 'Solar Vortex', mode: 'linear', angle: 140, colors: ['#020617', '#92400E', '#F59E0B', '#FDE047'] },
  { id: 'g005', name: 'Ultraviolet Core', mode: 'linear', angle: 150, colors: ['#020617', '#4C1D95', '#7C3AED', '#C4B5FD'] },
  { id: 'g006', name: 'Plasma Rose', mode: 'radial', angle: 90, colors: ['#020617', '#86198F', '#EC4899', '#F9A8D4'] },
  { id: 'g007', name: 'Arctic Neon', mode: 'linear', angle: 110, colors: ['#020617', '#0284C7', '#38BDF8', '#7DD3FC'] },
  { id: 'g008', name: 'Emerald Pulse', mode: 'linear', angle: 130, colors: ['#020617', '#065F46', '#10B981', '#6EE7B7'] },
  { id: 'g009', name: 'Inferno Drive', mode: 'linear', angle: 160, colors: ['#020617', '#7C2D12', '#EA580C', '#FB923C'] },
  { id: 'g010', name: 'Gamma Wave', mode: 'linear', angle: 145, colors: ['#020617', '#1E40AF', '#6366F1', '#A5B4FC'] },
  { id: 'g011', name: 'Neon Blaze', mode: 'linear', angle: 135, colors: ['#020617', '#7F1D1D', '#EF4444', '#FDE047'] },
  { id: 'g012', name: 'Cyber Teal', mode: 'linear', angle: 120, colors: ['#020617', '#0D9488', '#2DD4BF', '#5EEAD4'] },
  { id: 'g013', name: 'Cosmic Magenta', mode: 'radial', angle: 90, colors: ['#020617', '#9D174D', '#EC4899', '#FDA4AF'] },
  { id: 'g014', name: 'Deep Voltage', mode: 'linear', angle: 155, colors: ['#020617', '#312E81', '#4F46E5', '#818CF8'] },
  { id: 'g015', name: 'Solar Acid', mode: 'linear', angle: 100, colors: ['#020617', '#65A30D', '#A3E635', '#FACC15'] },
  { id: 'g016', name: 'Obsidian Flame', mode: 'linear', angle: 170, colors: ['#020617', '#450A0A', '#B91C1C', '#FB7185'] },
  { id: 'g017', name: 'Frozen Neon', mode: 'linear', angle: 110, colors: ['#020617', '#0369A1', '#0EA5E9', '#38BDF8'] },
  { id: 'g018', name: 'Purple Shock', mode: 'linear', angle: 145, colors: ['#020617', '#5B21B6', '#8B5CF6', '#C4B5FD'] },
  { id: 'g019', name: 'Amber Surge', mode: 'linear', angle: 130, colors: ['#020617', '#B45309', '#F59E0B', '#FDE68A'] },
  { id: 'g020', name: 'Toxic Mint', mode: 'linear', angle: 105, colors: ['#020617', '#047857', '#34D399', '#99F6E4'] },
  { id: 'g021', name: 'Neon Sakura', mode: 'linear', angle: 140, colors: ['#020617', '#9D174D', '#F472B6', '#FCE7F3'] },
  { id: 'g022', name: 'Blue Reactor', mode: 'linear', angle: 160, colors: ['#020617', '#1E3A8A', '#2563EB', '#60A5FA'] },
  { id: 'g023', name: 'Firestorm', mode: 'radial', angle: 90, colors: ['#020617', '#7C2D12', '#EA580C', '#FACC15'] },
  { id: 'g024', name: 'Cyber Lime', mode: 'linear', angle: 100, colors: ['#020617', '#3F6212', '#84CC16', '#BEF264'] },
  { id: 'g025', name: 'Violet Nova', mode: 'linear', angle: 150, colors: ['#020617', '#6D28D9', '#A78BFA', '#DDD6FE'] },
  { id: 'g026', name: 'Aqua Burst', mode: 'linear', angle: 120, colors: ['#020617', '#0E7490', '#22D3EE', '#67E8F9'] },
  { id: 'g027', name: 'Crimson Night', mode: 'linear', angle: 170, colors: ['#020617', '#7F1D1D', '#DC2626', '#FCA5A5'] },
  { id: 'g028', name: 'Electric Iris', mode: 'linear', angle: 135, colors: ['#020617', '#3730A3', '#6366F1', '#A5B4FC'] },
  { id: 'g029', name: 'Solar Shock', mode: 'linear', angle: 110, colors: ['#020617', '#92400E', '#F97316', '#FDE047'] },
  { id: 'g030', name: 'Neon Glacier', mode: 'linear', angle: 95, colors: ['#020617', '#075985', '#38BDF8', '#BAE6FD'] },
  { id: 'g031', name: 'Pink Plasma', mode: 'radial', angle: 90, colors: ['#020617', '#9D174D', '#EC4899', '#F9A8D4'] },
  { id: 'g032', name: 'Blue Pulse', mode: 'linear', angle: 145, colors: ['#020617', '#1E40AF', '#3B82F6', '#93C5FD'] },
  { id: 'g033', name: 'Emerald Shock', mode: 'linear', angle: 120, colors: ['#020617', '#065F46', '#10B981', '#34D399'] },
  { id: 'g034', name: 'Inferno Pink', mode: 'linear', angle: 160, colors: ['#020617', '#BE123C', '#FB7185', '#FDA4AF'] },
  { id: 'g035', name: 'Neon Indigo', mode: 'linear', angle: 135, colors: ['#020617', '#312E81', '#4F46E5', '#818CF8'] },
  { id: 'g036', name: 'Acid Wave', mode: 'linear', angle: 105, colors: ['#020617', '#4D7C0F', '#A3E635', '#D9F99D'] },
  { id: 'g037', name: 'Cyber Ocean', mode: 'linear', angle: 140, colors: ['#020617', '#0C4A6E', '#0284C7', '#38BDF8'] },
  { id: 'g038', name: 'Purple Heat', mode: 'linear', angle: 155, colors: ['#020617', '#581C87', '#A855F7', '#D8B4FE'] },
  { id: 'g039', name: 'Solar Ember', mode: 'linear', angle: 125, colors: ['#020617', '#9A3412', '#EA580C', '#FDBA74'] },
  { id: 'g040', name: 'Neon Frost', mode: 'linear', angle: 100, colors: ['#020617', '#0EA5E9', '#7DD3FC', '#E0F2FE'] },
  { id: 'g041', name: 'Voltage Red', mode: 'linear', angle: 150, colors: ['#020617', '#7F1D1D', '#EF4444', '#F87171'] },
  { id: 'g042', name: 'Cyber Blue', mode: 'linear', angle: 135, colors: ['#020617', '#1D4ED8', '#3B82F6', '#60A5FA'] },
  { id: 'g043', name: 'Neon Jungle', mode: 'linear', angle: 120, colors: ['#020617', '#166534', '#22C55E', '#4ADE80'] },
  { id: 'g044', name: 'Purple Storm', mode: 'linear', angle: 160, colors: ['#020617', '#581C87', '#9333EA', '#C084FC'] },
  { id: 'g045', name: 'Solar Burst', mode: 'linear', angle: 110, colors: ['#020617', '#92400E', '#F59E0B', '#FACC15'] },
  { id: 'g046', name: 'Crimson Core', mode: 'linear', angle: 170, colors: ['#020617', '#450A0A', '#DC2626', '#FB7185'] },
  { id: 'g047', name: 'Neon Aqua', mode: 'linear', angle: 100, colors: ['#020617', '#0EA5E9', '#22D3EE', '#67E8F9'] },
  { id: 'g048', name: 'Electric Violet', mode: 'linear', angle: 145, colors: ['#020617', '#4C1D95', '#7C3AED', '#A78BFA'] },
  { id: 'g049', name: 'Amber Flash', mode: 'linear', angle: 130, colors: ['#020617', '#B45309', '#F59E0B', '#FCD34D'] },
  { id: 'g050', name: 'Mint Voltage', mode: 'linear', angle: 105, colors: ['#020617', '#047857', '#10B981', '#6EE7B7'] },
  { id: 'g051', name: 'Neon Ruby', mode: 'linear', angle: 150, colors: ['#020617', '#7F1D1D', '#BE123C', '#F43F5E'] },
  { id: 'g052', name: 'Blue Nova', mode: 'linear', angle: 135, colors: ['#020617', '#1E40AF', '#2563EB', '#3B82F6'] },
  { id: 'g053', name: 'Green Reactor', mode: 'linear', angle: 120, colors: ['#020617', '#14532D', '#16A34A', '#22C55E'] },
  { id: 'g054', name: 'Violet Flash', mode: 'linear', angle: 160, colors: ['#020617', '#6D28D9', '#8B5CF6', '#A78BFA'] },
  { id: 'g055', name: 'Solar Spark', mode: 'linear', angle: 110, colors: ['#020617', '#92400E', '#F97316', '#FDBA74'] },
  { id: 'g056', name: 'Crimson Blaze', mode: 'linear', angle: 170, colors: ['#020617', '#7F1D1D', '#EF4444', '#FCA5A5'] },
  { id: 'g057', name: 'Aqua Charge', mode: 'linear', angle: 100, colors: ['#020617', '#0891B2', '#22D3EE', '#67E8F9'] },
  { id: 'g058', name: 'Indigo Pulse', mode: 'linear', angle: 145, colors: ['#020617', '#312E81', '#4F46E5', '#6366F1'] },
  { id: 'g059', name: 'Amber Nova', mode: 'linear', angle: 130, colors: ['#020617', '#B45309', '#F59E0B', '#FDE047'] },
  { id: 'g060', name: 'Mint Shock', mode: 'linear', angle: 105, colors: ['#020617', '#047857', '#34D399', '#99F6E4'] },
  { id: 'g061', name: 'Neon Cherry', mode: 'linear', angle: 150, colors: ['#020617', '#9F1239', '#E11D48', '#FB7185'] },
  { id: 'g062', name: 'Blue Surge', mode: 'linear', angle: 135, colors: ['#020617', '#1E3A8A', '#2563EB', '#60A5FA'] },
  { id: 'g063', name: 'Green Surge', mode: 'linear', angle: 120, colors: ['#020617', '#166534', '#22C55E', '#4ADE80'] },
  { id: 'g064', name: 'Purple Nova', mode: 'linear', angle: 160, colors: ['#020617', '#581C87', '#A855F7', '#D8B4FE'] },
  { id: 'g065', name: 'Solar Drive', mode: 'linear', angle: 110, colors: ['#020617', '#92400E', '#EA580C', '#FDBA74'] },
  { id: 'g066', name: 'Crimson Pulse', mode: 'linear', angle: 170, colors: ['#020617', '#7F1D1D', '#DC2626', '#F87171'] },
  { id: 'g067', name: 'Aqua Storm', mode: 'linear', angle: 100, colors: ['#020617', '#0E7490', '#0EA5E9', '#38BDF8'] },
  { id: 'g068', name: 'Indigo Storm', mode: 'linear', angle: 145, colors: ['#020617', '#312E81', '#4338CA', '#6366F1'] },
  { id: 'g069', name: 'Amber Drive', mode: 'linear', angle: 130, colors: ['#020617', '#92400E', '#F59E0B', '#FACC15'] },
  { id: 'g070', name: 'Mint Reactor', mode: 'linear', angle: 105, colors: ['#020617', '#065F46', '#10B981', '#34D399'] },
  { id: 'g071', name: 'Neon Flame', mode: 'linear', angle: 150, colors: ['#020617', '#7C2D12', '#EA580C', '#FB923C'] },
  { id: 'g072', name: 'Blue Flame', mode: 'linear', angle: 135, colors: ['#020617', '#1E40AF', '#3B82F6', '#93C5FD'] },
  { id: 'g073', name: 'Green Flame', mode: 'linear', angle: 120, colors: ['#020617', '#14532D', '#16A34A', '#22C55E'] },
  { id: 'g074', name: 'Purple Flame', mode: 'linear', angle: 160, colors: ['#020617', '#581C87', '#9333EA', '#C084FC'] },
  { id: 'g075', name: 'Solar Flame', mode: 'linear', angle: 110, colors: ['#020617', '#92400E', '#F97316', '#FDBA74'] },
  { id: 'g076', name: 'Crimson Flame', mode: 'linear', angle: 170, colors: ['#020617', '#7F1D1D', '#EF4444', '#FCA5A5'] },
  { id: 'g077', name: 'Aqua Flame', mode: 'linear', angle: 100, colors: ['#020617', '#0891B2', '#22D3EE', '#67E8F9'] },
  { id: 'g078', name: 'Indigo Flame', mode: 'linear', angle: 145, colors: ['#020617', '#312E81', '#4F46E5', '#818CF8'] },
  { id: 'g079', name: 'Amber Flame', mode: 'linear', angle: 130, colors: ['#020617', '#B45309', '#F59E0B', '#FDE047'] },
  { id: 'g080', name: 'Mint Flame', mode: 'linear', angle: 105, colors: ['#020617', '#047857', '#34D399', '#6EE7B7'] },
  { id: 'g081', name: 'Neon Core', mode: 'linear', angle: 150, colors: ['#020617', '#1E40AF', '#7C3AED', '#FACC15'] },
  { id: 'g082', name: 'Red Core', mode: 'linear', angle: 135, colors: ['#020617', '#7F1D1D', '#DC2626', '#FB7185'] },
  { id: 'g083', name: 'Green Core', mode: 'linear', angle: 120, colors: ['#020617', '#14532D', '#22C55E', '#4ADE80'] },
  { id: 'g084', name: 'Purple Core', mode: 'linear', angle: 160, colors: ['#020617', '#581C87', '#A855F7', '#D8B4FE'] },
  { id: 'g085', name: 'Solar Core', mode: 'linear', angle: 110, colors: ['#020617', '#92400E', '#F59E0B', '#FACC15'] },
  { id: 'g086', name: 'Crimson Core X', mode: 'linear', angle: 170, colors: ['#020617', '#450A0A', '#DC2626', '#F87171'] },
  { id: 'g087', name: 'Aqua Core', mode: 'linear', angle: 100, colors: ['#020617', '#0E7490', '#22D3EE', '#67E8F9'] },
  { id: 'g088', name: 'Indigo Core', mode: 'linear', angle: 145, colors: ['#020617', '#312E81', '#4F46E5', '#6366F1'] },
  { id: 'g089', name: 'Amber Core X', mode: 'linear', angle: 130, colors: ['#020617', '#B45309', '#F59E0B', '#FCD34D'] },
  { id: 'g090', name: 'Mint Core', mode: 'linear', angle: 105, colors: ['#020617', '#065F46', '#10B981', '#6EE7B7'] },
  { id: 'g091', name: 'Neon Zenith', mode: 'linear', angle: 150, colors: ['#020617', '#1E40AF', '#7C3AED', '#EC4899'] },
  { id: 'g092', name: 'Red Zenith', mode: 'linear', angle: 135, colors: ['#020617', '#7F1D1D', '#EF4444', '#FCA5A5'] },
  { id: 'g093', name: 'Green Zenith', mode: 'linear', angle: 120, colors: ['#020617', '#14532D', '#22C55E', '#86EFAC'] },
  { id: 'g094', name: 'Purple Zenith', mode: 'linear', angle: 160, colors: ['#020617', '#581C87', '#9333EA', '#C084FC'] },
  { id: 'g095', name: 'Solar Zenith', mode: 'linear', angle: 110, colors: ['#020617', '#92400E', '#EA580C', '#FDBA74'] },
  { id: 'g096', name: 'Crimson Zenith', mode: 'linear', angle: 170, colors: ['#020617', '#450A0A', '#DC2626', '#FB7185'] },
  { id: 'g097', name: 'Aqua Zenith', mode: 'linear', angle: 100, colors: ['#020617', '#0891B2', '#22D3EE', '#67E8F9'] },
  { id: 'g098', name: 'Indigo Zenith', mode: 'linear', angle: 145, colors: ['#020617', '#312E81', '#4F46E5', '#818CF8'] },
  { id: 'g099', name: 'Amber Zenith', mode: 'linear', angle: 130, colors: ['#020617', '#B45309', '#F59E0B', '#FDE047'] },
  { id: 'g100', name: 'Mint Zenith', mode: 'linear', angle: 105, colors: ['#020617', '#047857', '#10B981', '#34D399'] }
]

const colors = ref(
  baseSeed.map(hex => ({
    id: idCounter++,
    hex,
    name: generateColorName(hex),
    custom: false
  }))
)

const normalizedColors = computed(() => {
  const list = colors.value.map(c => ({ hex: normalizeHex(c.hex) })).filter(c => c.hex && c.hex.length >= 4)
  if (!list.length) return baseSeed.map(h => ({ hex: h }))
  if (list.length === 1) return [list[0], list[0]]
  return list
})

const gradientStops = computed(() => {
  const list = normalizedColors.value
  const n = list.length
  if (!n) return []
  return list.map((c, idx) => ({
    color: c.hex,
    position: (idx / (n - 1 || 1)) * 100
  }))
})

const gradientString = computed(() => {
  const stops = gradientStops.value
  if (!stops.length) return 'linear-gradient(90deg,#0F172A,#0EA5E9)'
  const stopsStr = stops.map(s => `${s.color} ${s.position.toFixed(1)}%`).join(', ')
  if (gradientMode.value === 'radial') return `radial-gradient(circle at 50% 50%, ${stopsStr})`
  return `linear-gradient(${gradientAngle.value}deg, ${stopsStr})`
})

const filteredPresets = computed(() => {
  const q = presetQuery.value.trim().toLowerCase()
  if (!q) return gradientPresets
  return gradientPresets.filter(p => {
    const a = (p.name || '').toLowerCase().includes(q)
    const b = (p.mode || '').toLowerCase().includes(q)
    const c = (p.colors || []).some(x => String(x).toLowerCase().includes(q))
    return a || b || c
  })
})

function syncThemeFlag() {
  isDark.value = document.documentElement.classList.contains('dark')
}

function normalizeHex(value) {
  if (!value) return ''
  let v = String(value).trim()
  if (!v.startsWith('#')) v = `#${v}`
  v = v.slice(0, 7)
  const hex = v.replace(/[^#0-9a-fA-F]/g, '')
  return hex.toUpperCase()
}

function hexToRgb(hex) {
  const v = normalizeHex(hex)
  if (v.length !== 7) return { r: 0, g: 0, b: 0 }
  const r = parseInt(v.slice(1, 3), 16)
  const g = parseInt(v.slice(3, 5), 16)
  const b = parseInt(v.slice(5, 7), 16)
  return { r, g, b }
}

function rgbLabel(hex) {
  const v = normalizeHex(hex)
  if (v.length !== 7) return 'rgb(?, ?, ?)'
  const { r, g, b } = hexToRgb(v)
  return `rgb(${r},${g},${b})`
}

function rgbToHsl(r, g, b) {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h, s
  const l = (max + min) / 2
  if (max === min) {
    h = 0
    s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      default:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }
  return { h: h * 360, s: s * 100, l: l * 100 }
}

function generateColorName(hex) {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  if (s < 8 && l < 18) return 'Deep Obsidian'
  if (s < 8 && l < 30) return 'Charcoal Mist'
  if (s < 8 && l < 55) return 'Soft Graphite'
  if (s < 8 && l < 78) return 'Clouded Grey'
  if (s < 8) return 'Paper White'
  const tone = l < 22 ? 'Midnight' : l < 38 ? 'Deep' : l < 55 ? 'Rich' : l < 72 ? 'Soft' : l < 88 ? 'Pale' : 'Airy'
  const family =
    h < 15 || h >= 345 ? 'Crimson' :
      h < 35 ? 'Amber' :
        h < 65 ? 'Gold' :
          h < 110 ? 'Verdant' :
            h < 150 ? 'Mint' :
              h < 190 ? 'Teal' :
                h < 225 ? 'Sky' :
                  h < 255 ? 'Azure' :
                    h < 285 ? 'Indigo' :
                      h < 315 ? 'Violet' :
                        h < 335 ? 'Magenta' :
                          'Rose'
  return `${tone} ${family}`
}

function onHexInput(index, value) {
  const hex = normalizeHex(value)
  const item = colors.value[index]
  item.hex = hex
  if (!item.custom && hex.length === 7) item.name = generateColorName(hex)
}

function onColorPicker(index, value) {
  const hex = normalizeHex(value)
  const item = colors.value[index]
  if (!hex) return
  item.hex = hex
  if (!item.custom && hex.length === 7) item.name = generateColorName(hex)
}

function addColor() {
  if (colors.value.length >= 6) return
  const last = colors.value[colors.value.length - 1]
  const base = last?.hex || '#64748B'
  colors.value.push({ id: idCounter++, hex: base, name: generateColorName(base), custom: false })
}

function removeColor(index) {
  if (colors.value.length <= 2) return
  colors.value.splice(index, 1)
}

function moveColor(index, delta) {
  const target = index + delta
  if (target < 0 || target >= colors.value.length) return
  const arr = colors.value.slice()
  const [item] = arr.splice(index, 1)
  arr.splice(target, 0, item)
  colors.value = arr
}

function resetPalette() {
  colors.value = baseSeed.map(hex => ({ id: idCounter++, hex, name: generateColorName(hex), custom: false }))
  gradientMode.value = 'linear'
  gradientAngle.value = 135
  presetQuery.value = ''
  showPresetModal.value = false
}

function randomizeAngle() {
  gradientAngle.value = Math.floor(Math.random() * 360)
}

function markCustom(index) {
  colors.value[index].custom = true
}

function findNameForHex(hex) {
  const item = colors.value.find(c => normalizeHex(c.hex) === normalizeHex(hex))
  return item?.name || ''
}

function buildPresetGradient(preset) {
  const colorsArr = preset.colors || []
  if (!colorsArr.length) return 'linear-gradient(90deg,#000,#fff)'
  const stopsStr = colorsArr.map((c, idx) => `${normalizeHex(c)} ${(idx / (colorsArr.length - 1)) * 100}%`).join(', ')
  if (preset.mode === 'radial') return `radial-gradient(circle at 50% 50%, ${stopsStr})`
  return `linear-gradient(${preset.angle || 90}deg, ${stopsStr})`
}

function applyPreset(preset) {
  gradientMode.value = preset.mode || 'linear'
  if (preset.mode !== 'radial') gradientAngle.value = preset.angle || 90
  const palette = (preset.colors || []).slice(0, 6).map(normalizeHex)
  if (palette.length) {
    colors.value = palette.map(hex => ({ id: idCounter++, hex, name: generateColorName(hex), custom: false }))
  }
  showPresetModal.value = false
  mobileMenuOpen.value = false
}

async function exportGradientPng() {
  const title = 'gradient-render'
  const W = 2400
  const H = Math.round(W * (9 / 21))
  const radius = 48

  const dpr = Math.max(2, Math.min(3, Math.floor(window.devicePixelRatio || 2)))
  const canvas = document.createElement('canvas')
  canvas.width = W * dpr
  canvas.height = H * dpr
  const ctx = canvas.getContext('2d')
  ctx.scale(dpr, dpr)

  ctx.clearRect(0, 0, W, H)

  ctx.save()
  roundedRect(ctx, 0, 0, W, H, radius)
  ctx.clip()

  paintGradientToCanvas(ctx, 0, 0, W, H, gradientMode.value, gradientAngle.value, gradientStops.value)
  addSoftNoise(ctx, 0, 0, W, H, isDark.value ? 0.07 : 0.04, isDark.value ? 'overlay' : 'soft-light')

  ctx.restore()

  await downloadBlob(canvas, `${title}.png`)
}

async function exportPng() {
  const title = 'Gradient Board'

  const cols = 2
  const gap = 18
  const pad = 56

  const topBlock = 132
  const bannerH = 210

  const cardW = 520
  const cardH = 130

  const list = normalizedColors.value.map(x => normalizeHex(x.hex)).filter(Boolean)
  const steps = list.length ? list : baseSeed.map(normalizeHex)

  const rows = Math.ceil(steps.length / cols)
  const gridW = cols * cardW + (cols - 1) * gap
  const gridH = rows * cardH + (rows - 1) * gap

  const W = pad * 2 + gridW
  const H = pad * 2 + topBlock + bannerH + 26 + gridH + 24

  const dpr = Math.max(2, Math.min(3, Math.floor(window.devicePixelRatio || 2)))
  const canvas = document.createElement('canvas')
  canvas.width = W * dpr
  canvas.height = H * dpr
  const ctx = canvas.getContext('2d')
  ctx.scale(dpr, dpr)

  const dark = document.documentElement.classList.contains('dark')
  const bgTop = dark ? '#09090B' : '#FFFFFF'
  const bgBottom = dark ? '#050507' : '#FAFAFA'

  const bg = ctx.createLinearGradient(0, 0, 0, H)
  bg.addColorStop(0, bgTop)
  bg.addColorStop(1, bgBottom)
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, W, H)

  drawGlow(ctx, W - 240, 190, 320, dark ? 'rgba(168,85,247,.14)' : 'rgba(168,85,247,.10)')
  drawGlow(ctx, 210, H - 220, 360, dark ? 'rgba(99,102,241,.12)' : 'rgba(99,102,241,.08)')

  const badgeText = 'Generated with NexGridCo'
  ctx.save()
  ctx.font = '800 10px Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
  const badgePadX = 12
  const badgeW = ctx.measureText(badgeText.toUpperCase()).width + badgePadX * 2
  const badgeH = 24
  const badgeX = pad
  const badgeY = pad

  roundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 999)
  ctx.fillStyle = dark ? 'rgba(99,102,241,0.14)' : 'rgba(99,102,241,0.10)'
  ctx.fill()
  ctx.strokeStyle = dark ? 'rgba(99,102,241,0.26)' : 'rgba(99,102,241,0.22)'
  ctx.stroke()

  ctx.fillStyle = dark ? '#A5B4FC' : '#4F46E5'
  ctx.fillText(badgeText.toUpperCase(), badgeX + badgePadX, badgeY + 16)
  ctx.restore()

  ctx.save()
  ctx.fillStyle = dark ? '#FAFAFA' : '#09090B'
  ctx.font = '800 36px Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
  ctx.fillText(title, pad, pad + 78)
  ctx.restore()

  ctx.save()
  ctx.fillStyle = dark ? 'rgba(161,161,170,.9)' : 'rgba(82,82,91,.95)'
  ctx.font = '600 12px Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
  const meta = gradientMode.value === 'radial' ? 'Radial • circle' : `Linear • ${gradientAngle.value}°`
  ctx.fillText(meta, pad, pad + 102)
  ctx.restore()

  const bannerX = pad
  const bannerY = pad + topBlock
  const bannerW = gridW
  const bannerR = 26

  drawCardShadow(ctx, bannerX, bannerY, bannerW, bannerH, bannerR, dark)

  ctx.save()
  roundedRect(ctx, bannerX, bannerY, bannerW, bannerH, bannerR)
  ctx.clip()
  paintGradientToCanvas(ctx, bannerX, bannerY, bannerW, bannerH, gradientMode.value, gradientAngle.value, gradientStops.value)
  addSoftNoise(ctx, bannerX, bannerY, bannerW, bannerH, dark ? 0.07 : 0.04, dark ? 'overlay' : 'soft-light')
  ctx.restore()

  ctx.save()
  roundedRect(ctx, bannerX, bannerY, bannerW, bannerH, bannerR)
  ctx.strokeStyle = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  ctx.stroke()
  ctx.restore()

  const startX = pad
  const startY = bannerY + bannerH + 26

  steps.forEach((hex, idx) => {
    const r = Math.floor(idx / cols)
    const c = idx % cols
    const x = startX + c * (cardW + gap)
    const y = startY + r * (cardH + gap)

    const rr = 22

    drawCardShadow(ctx, x, y, cardW, cardH, rr, dark)

    ctx.save()
    roundedRect(ctx, x, y, cardW, cardH, rr)
    ctx.fillStyle = dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'
    ctx.fill()
    ctx.restore()

    const sw = 92
    const sx = x + 18
    const sy = y + 19
    const sr = 18

    ctx.save()
    roundedRect(ctx, sx, sy, sw, sw, sr)
    ctx.clip()
    ctx.fillStyle = hex
    ctx.fillRect(sx, sy, sw, sw)
    ctx.restore()

    ctx.save()
    roundedRect(ctx, sx, sy, sw, sw, sr)
    ctx.strokeStyle = dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'
    ctx.stroke()
    ctx.restore()

    const name = findNameForHex(hex) || generateColorName(hex)
    ctx.save()
    ctx.fillStyle = dark ? '#FAFAFA' : '#0A0A0B'
    ctx.font = '800 16px Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
    ctx.fillText(name, x + 18 + sw + 16, y + 52)

    ctx.fillStyle = dark ? 'rgba(228,228,231,.92)' : 'rgba(82,82,91,.95)'
    ctx.font = '600 14px "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
    ctx.fillText(hex.toUpperCase(), x + 18 + sw + 16, y + 78)

    const { r: rr2, g: gg2, b: bb2 } = hexToRgb(hex)
    ctx.fillStyle = dark ? 'rgba(161,161,170,.85)' : 'rgba(113,113,122,.95)'
    ctx.font = '600 12px Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
    ctx.fillText(`rgb(${rr2}, ${gg2}, ${bb2})`, x + 18 + sw + 16, y + 102)
    ctx.restore()

    ctx.save()
    roundedRect(ctx, x, y, cardW, cardH, rr)
    ctx.strokeStyle = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
    ctx.stroke()
    ctx.restore()
  })

  await downloadBlob(canvas, `${sanitizeFilename(title)}.png`)
}

function paintGradientToCanvas(ctx, x, y, w, h, mode, angle, stops) {
  const st = (stops || []).map(s => ({
    color: normalizeHex(s.color),
    position: Math.max(0, Math.min(1, (s.position || 0) / 100))
  })).filter(s => s.color && s.color.length === 7)

  if (!st.length) {
    ctx.fillStyle = '#0EA5E9'
    ctx.fillRect(x, y, w, h)
    return
  }

  if (mode === 'radial') {
    const g = ctx.createRadialGradient(
      x + w * 0.5, y + h * 0.5, 0,
      x + w * 0.5, y + h * 0.5, Math.max(w, h) * 0.65
    )
    st.forEach(s => g.addColorStop(s.position, s.color))
    ctx.fillStyle = g
    ctx.fillRect(x, y, w, h)
    return
  }

  const a = ((Number(angle || 0) % 360) * Math.PI) / 180
  const ux = Math.sin(a)
  const uy = -Math.cos(a)

  const cx = x + w / 2
  const cy = y + h / 2

  const corners = [
    { x: x, y: y },
    { x: x + w, y: y },
    { x: x, y: y + h },
    { x: x + w, y: y + h }
  ]

  let tMin = Infinity
  let tMax = -Infinity

  for (const p of corners) {
    const t = (p.x - cx) * ux + (p.y - cy) * uy
    if (t < tMin) tMin = t
    if (t > tMax) tMax = t
  }

  const x1 = cx + ux * tMin
  const y1 = cy + uy * tMin
  const x2 = cx + ux * tMax
  const y2 = cy + uy * tMax

  const g = ctx.createLinearGradient(x1, y1, x2, y2)
  st.forEach(s => g.addColorStop(s.position, s.color))
  ctx.fillStyle = g
  ctx.fillRect(x, y, w, h)
}


function addSoftNoise(ctx, x, y, w, h, alpha = 0.06, mode = 'overlay') {
  const tile = 220
  const noise = document.createElement('canvas')
  noise.width = tile
  noise.height = tile
  const nctx = noise.getContext('2d')

  const img = nctx.createImageData(tile, tile)
  const d = img.data

  for (let i = 0; i < d.length; i += 4) {
    const v = (Math.random() * 255) | 0
    d[i] = v
    d[i + 1] = v
    d[i + 2] = v
    d[i + 3] = 255
  }
  nctx.putImageData(img, 0, 0)

  ctx.save()
  ctx.globalAlpha = alpha
  ctx.globalCompositeOperation = mode
  const pattern = ctx.createPattern(noise, 'repeat')
  ctx.fillStyle = pattern
  ctx.fillRect(x, y, w, h)
  ctx.restore()
}

function roundedRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

function drawGlow(ctx, cx, cy, size, color) {
  ctx.save()
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, size)
  g.addColorStop(0, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(cx, cy, size, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function drawCardShadow(ctx, x, y, w, h, r, dark) {
  ctx.save()
  ctx.shadowColor = dark ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.18)'
  ctx.shadowBlur = dark ? 28 : 20
  ctx.shadowOffsetY = dark ? 12 : 10
  ctx.shadowOffsetX = 0
  roundedRect(ctx, x, y, w, h, r)
  ctx.fillStyle = 'rgba(0,0,0,0)'
  ctx.fill()
  ctx.restore()
}

function sanitizeFilename(name) {
  return String(name || 'export')
    .trim()
    .replace(/\/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 80)
}

function downloadBlob(canvas, filename) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) return resolve()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      resolve()
    }, 'image/png')
  })
}

let observer = null

onMounted(() => {
  syncThemeFlag()
  observer = new MutationObserver(() => syncThemeFlag())
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>


<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 999px;
  transition: background-color 0.2s;
}

:global(.dark) .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.18);
}

:global(.dark) .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

.noise-bg {
  background-image: url('https://grainy-gradients.vercel.app/noise.svg');
  background-size: 260px 260px;
  background-repeat: repeat;
}

.noise-soft {
  background-image: url('https://grainy-gradients.vercel.app/noise.svg');
  background-size: 220px 220px;
  background-repeat: repeat;
  opacity: 0.22;
  mix-blend-mode: overlay;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.list-leave-active {
  position: absolute;
  width: 100%;
}

@keyframes pulse-slow {

  0%,
  100% {
    opacity: 0.7;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(0.95);
  }
}

.animate-pulse-slow {
  animation: pulse-slow 3s infinite ease-in-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-fade-in {
  animation: fadeIn 0.4s ease-out forwards;
}
</style>